import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { 
  TrendingUp, Users, Euro, Calendar, BarChart3, 
  Loader2, RefreshCw, ArrowUpRight, ArrowDownRight,
  Award, CheckCircle, Megaphone
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [compareMode, setCompareMode] = useState(true);
  const [influencerRankings, setInfluencerRankings] = useState([]);
  const [customDates, setCustomDates] = useState({
    start: '',
    end: ''
  });

  useEffect(() => {
    fetchAnalytics();
    api.get('/admin/influencer-rankings').then(r => setInfluencerRankings(r.data.rankings || [])).catch(() => {});
  }, [period, compareMode]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      let url = `/admin/analytics?period=${period}&compare_with_previous=${compareMode}`;
      
      if (period === 'custom' && customDates.start && customDates.end) {
        url += `&start_date=${customDates.start}&end_date=${customDates.end}`;
      }
      
      const response = await api.get(url);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateSubmit = () => {
    if (customDates.start && customDates.end) {
      fetchAnalytics();
    }
  };

  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  const COLORS = ['#00FFFF', '#00FF88', '#FFB800', '#FF6B35', '#667eea'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center text-[#B8B8BA] py-12">
        Aucune donnée disponible
      </div>
    );
  }

  const { summary, top_vendors, top_clients, charts } = analytics;
  
  // Calculer les variations
  const clientsChange = calculatePercentageChange(
    summary.new_clients_current_period,
    summary.new_clients_previous_period
  );
  const revenueChange = calculatePercentageChange(
    summary.revenue_current_period,
    summary.revenue_previous_period
  );
  const transactionsChange = calculatePercentageChange(
    summary.transactions_current_period,
    summary.transactions_previous_period
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header avec sélecteurs */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">📊 Analytics</h1>
          <p className="text-[#B8B8BA]">Statistiques et performances de la plateforme</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#00FFFF] focus:outline-none"
          >
            <option value="7d">7 derniers jours</option>
            <option value="30d">30 derniers jours</option>
            <option value="90d">90 derniers jours</option>
            <option value="365d">Dernière année</option>
            <option value="custom">Période personnalisée</option>
          </select>
          
          <label className="flex items-center gap-2 text-white cursor-pointer">
            <input
              type="checkbox"
              checked={compareMode}
              onChange={(e) => setCompareMode(e.target.checked)}
              className="w-4 h-4 rounded border-[#3A3A3D] bg-[#1A1A1D] checked:bg-[#00FFFF]"
            />
            <span className="text-sm">Comparer avec période précédente</span>
          </label>
          
          <button
            onClick={fetchAnalytics}
            className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg hover:border-[#00FFFF] transition-colors text-white"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Dates personnalisées */}
      {period === 'custom' && (
        <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-4 flex items-center gap-4">
          <input
            type="date"
            value={customDates.start}
            onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
            className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#00FFFF] focus:outline-none"
          />
          <span className="text-[#B8B8BA]">à</span>
          <input
            type="date"
            value={customDates.end}
            onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
            className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#00FFFF] focus:outline-none"
          />
          <button
            onClick={handleCustomDateSubmit}
            className="px-6 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:shadow-lg transition-all"
          >
            Appliquer
          </button>
        </div>
      )}

      {/* Cartes KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Clients Totaux */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Users className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-[#B8B8BA] text-sm">Clients Totaux</p>
                <p className="text-3xl font-bold text-white">{summary.total_clients}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[#B8B8BA]">Nouveaux :</span>
            <span className="text-[#00FF88] font-semibold">{summary.new_clients_current_period}</span>
            {clientsChange && (
              <span className={`flex items-center gap-1 ${parseFloat(clientsChange) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {parseFloat(clientsChange) >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                {Math.abs(clientsChange)}%
              </span>
            )}
          </div>
        </motion.div>

        {/* Revenus */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Euro className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-[#B8B8BA] text-sm">Revenus Période</p>
                <p className="text-3xl font-bold text-white">{summary.revenue_current_period.toFixed(0)}€</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[#B8B8BA]">Transactions :</span>
            <span className="text-[#00FF88] font-semibold">{summary.transactions_current_period}</span>
            {revenueChange && (
              <span className={`flex items-center gap-1 ${parseFloat(revenueChange) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {parseFloat(revenueChange) >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                {Math.abs(revenueChange)}%
              </span>
            )}
          </div>
        </motion.div>

        {/* Abonnements Actifs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <CheckCircle className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-[#B8B8BA] text-sm">Abonnements Actifs</p>
                <p className="text-3xl font-bold text-white">{summary.active_subscriptions}</p>
              </div>
            </div>
          </div>
          <div className="text-sm text-[#B8B8BA]">
            Expirés : <span className="text-red-400">{summary.expired_subscriptions}</span>
          </div>
        </motion.div>

        {/* Vendeurs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <Award className="h-6 w-6 text-orange-400" />
              </div>
              <div>
                <p className="text-[#B8B8BA] text-sm">Vendeurs Actifs</p>
                <p className="text-3xl font-bold text-white">{summary.total_vendors}</p>
              </div>
            </div>
          </div>
          <div className="text-sm text-[#B8B8BA]">
            En attente : <span className="text-yellow-400">{summary.pending_vendors}</span>
          </div>
        </motion.div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Évolution des Clients */}
        <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Users className="h-5 w-5 text-[#00FFFF]" />
            Évolution des Nouveaux Clients
          </h3>
          {charts.clients_evolution && charts.clients_evolution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={charts.clients_evolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3A3A3D" />
                <XAxis dataKey="date" stroke="#B8B8BA" tick={{ fontSize: 12 }} />
                <YAxis stroke="#B8B8BA" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#16161A', 
                    border: '1px solid #3A3A3D',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#00FFFF" 
                  strokeWidth={3}
                  dot={{ fill: '#00FFFF', r: 4 }}
                  name="Nouveaux clients"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-[#B8B8BA] py-12">Aucune donnée pour cette période</div>
          )}
        </div>

        {/* Évolution des Revenus */}
        <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Euro className="h-5 w-5 text-[#00FF88]" />
            Évolution des Revenus
          </h3>
          {charts.revenue_evolution && charts.revenue_evolution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={charts.revenue_evolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3A3A3D" />
                <XAxis dataKey="date" stroke="#B8B8BA" tick={{ fontSize: 12 }} />
                <YAxis stroke="#B8B8BA" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#16161A', 
                    border: '1px solid #3A3A3D',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="revenue" fill="#00FF88" name="Revenus (€)" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-[#B8B8BA] py-12">Aucune donnée pour cette période</div>
          )}
        </div>
      </div>

      {/* Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Vendeurs */}
        <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Award className="h-5 w-5 text-[#FFB800]" />
            Top 10 Vendeurs
          </h3>
          {top_vendors && top_vendors.length > 0 ? (
            <div className="space-y-3">
              {top_vendors.map((vendor, index) => (
                <div 
                  key={vendor.vendor_id} 
                  className="flex items-center justify-between p-3 bg-[#222226] rounded-lg hover:bg-[#2A2A2E] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                      index === 1 ? 'bg-gray-400/20 text-gray-400' :
                      index === 2 ? 'bg-orange-500/20 text-orange-400' :
                      'bg-[#3A3A3D] text-[#B8B8BA]'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-white font-medium">{vendor.vendor_name}</p>
                      <p className="text-[#B8B8BA] text-sm">{vendor.vendor_email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#00FF88] font-bold">{vendor.sales_count} ventes</p>
                    <p className="text-[#B8B8BA] text-sm">{vendor.total_commission.toFixed(2)}€ commission</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-[#B8B8BA] py-8">Aucun vendeur pour cette période</div>
          )}
        </div>

        {/* Top Clients */}
        <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#FF6B35]" />
            Top 10 Clients (Crédit Gagné)
          </h3>
          {top_clients && top_clients.length > 0 ? (
            <div className="space-y-3">
              {top_clients.map((client, index) => (
                <div 
                  key={client.client_email} 
                  className="flex items-center justify-between p-3 bg-[#222226] rounded-lg hover:bg-[#2A2A2E] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                      index === 1 ? 'bg-gray-400/20 text-gray-400' :
                      index === 2 ? 'bg-orange-500/20 text-orange-400' :
                      'bg-[#3A3A3D] text-[#B8B8BA]'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-white font-medium">{client.client_name}</p>
                      <p className="text-[#B8B8BA] text-sm">{client.client_email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#FF6B35] font-bold">{client.total_credit.toFixed(2)}€</p>
                    <p className="text-[#B8B8BA] text-sm">total gagné</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-[#B8B8BA] py-8">Aucun client avec crédit</div>
          )}
        </div>
      </div>

      {/* Résumé Global */}
      <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[#667eea]" />
          Résumé de la Période
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-[#B8B8BA] text-sm mb-1">Revenus Total (All Time)</p>
            <p className="text-2xl font-bold text-[#00FFFF]">{summary.total_revenue_all_time.toFixed(0)}€</p>
          </div>
          <div>
            <p className="text-[#B8B8BA] text-sm mb-1">Crédit Utilisé (Période)</p>
            <p className="text-2xl font-bold text-[#FFB800]">{summary.credit_used_current_period.toFixed(0)}€</p>
          </div>
          <div>
            <p className="text-[#B8B8BA] text-sm mb-1">Abonnements En Attente</p>
            <p className="text-2xl font-bold text-yellow-400">{summary.pending_subscriptions}</p>
          </div>
          <div>
            <p className="text-[#B8B8BA] text-sm mb-1">Taux de Conversion</p>
            <p className="text-2xl font-bold text-[#FF6B35]">
              {summary.total_clients > 0 
                ? ((summary.active_subscriptions / summary.total_clients) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Classement Influenceurs */}
      {influencerRankings.length > 0 && (
        <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6" data-testid="influencer-rankings-analytics">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#FF6B35]" />
            Classement Influenceurs — Revenus Générés
          </h3>
          <div className="space-y-3">
            {influencerRankings.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-3 px-4 bg-[#222226] rounded-lg border border-[#3A3A3D]/30" data-testid={`analytics-rank-${i}`}>
                <div className="flex items-center gap-4">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
                    i === 0 ? 'bg-[#FFB800]/20 text-[#FFB800]' : i === 1 ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]' : i === 2 ? 'bg-[#CD7F32]/20 text-[#CD7F32]' : 'bg-[#3A3A3D]/30 text-[#6B6B6E]'
                  }`}>#{r.rank}</span>
                  <div>
                    <p className="text-white font-medium text-sm">{r.pseudo}</p>
                    <p className="text-[#6B6B6E] text-xs">{r.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <p className="text-[#6B6B6E] text-xs">Ventes</p>
                    <p className="text-white font-bold text-sm">{r.total_sales}</p>
                  </div>
                  <div>
                    <p className="text-[#6B6B6E] text-xs">Revenus générés</p>
                    <p className="text-[#00FFFF] font-bold text-sm">{r.total_amount?.toFixed(2)}€</p>
                  </div>
                  <div>
                    <p className="text-[#6B6B6E] text-xs">Commissions</p>
                    <p className="text-[#00FF88] font-bold text-sm">{r.total_commission?.toFixed(2)}€</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
